import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DatabaseFieldMapping {
  id: string;
  user_id: string;
  customer_id: string;
  module_name: string;
  mapping: any; // JSON from database
  dialect: any; // JSON from database
  created_at: string;
  updated_at: string;
  is_default: boolean;
  template_name: string | null;
}

export interface StoredFieldMapping {
  id: string;
  user_id: string;
  customer_id: string;
  module_name: string;
  mapping: Record<string, string>;
  dialect: {
    delimiter: string;
    encoding: string;
    hasHeader: boolean;
  };
  created_at: string;
  updated_at: string;
  is_default: boolean;
  template_name?: string;
}

function transformDatabaseMapping(dbMapping: DatabaseFieldMapping): StoredFieldMapping {
  return {
    ...dbMapping,
    mapping: dbMapping.mapping as Record<string, string>,
    dialect: dbMapping.dialect as {
      delimiter: string;
      encoding: string;
      hasHeader: boolean;
    },
    template_name: dbMapping.template_name || undefined
  };
}

export function useFieldMappings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const saveMapping = useCallback(async (
    customerId: string,
    moduleName: string,
    mapping: Record<string, string>,
    dialect: { delimiter: string; encoding: string; hasHeader: boolean },
    templateName?: string
  ): Promise<StoredFieldMapping | null> => {
    try {
      setLoading(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('field_mappings')
        .upsert({
          user_id: user.user.id,
          customer_id: customerId,
          module_name: moduleName,
          mapping,
          dialect,
          template_name: templateName,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,customer_id,module_name'
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Mappning sparad",
        description: `Fältmappning för ${customerId} har sparats`
      });

      return transformDatabaseMapping(data as DatabaseFieldMapping);
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast({
        title: "Fel vid sparning",
        description: error instanceof Error ? error.message : "Kunde inte spara mappning",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const getMapping = useCallback(async (
    customerId: string,
    moduleName: string
  ): Promise<StoredFieldMapping | null> => {
    try {
      setLoading(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Get all module aliases for backward compatibility
      const { getModuleAliases } = await import('@/lib/modules')
      const moduleAliases = getModuleAliases(moduleName)
      const moduleNameFilter = moduleAliases.map(a => `module_name.eq.${a}`).join(',')
      
      const { data, error } = await supabase
        .from('field_mappings')
        .select('*')
        .eq('user_id', user.user.id)
        .eq('customer_id', customerId)
        .or(moduleNameFilter)
        .maybeSingle();

      if (error) {
        console.error('Error getting mapping:', error)
        throw error;
      }

      return data ? transformDatabaseMapping(data as DatabaseFieldMapping) : null;
    } catch (error) {
      console.error('Error getting mapping:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserMappings = useCallback(async (moduleName?: string): Promise<StoredFieldMapping[]> => {
    try {
      setLoading(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      let query = supabase
        .from('field_mappings')
        .select('*')
        .eq('user_id', user.user.id)
        .order('updated_at', { ascending: false });

      if (moduleName) {
        // Get all module aliases for backward compatibility
        const { getModuleAliases } = await import('@/lib/modules')
        const moduleAliases = getModuleAliases(moduleName)
        const moduleNameFilter = moduleAliases.map(a => `module_name.eq.${a}`).join(',')
        query = query.or(moduleNameFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((item: any) => transformDatabaseMapping(item as DatabaseFieldMapping));
    } catch (error) {
      console.error('Error getting user mappings:', error);
      toast({
        title: "Fel vid hämtning",
        description: "Kunde inte hämta sparade mappningar",
        variant: "destructive"
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const deleteMapping = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('field_mappings')
        .delete()
        .eq('id', id)
        .eq('user_id', user.user.id); // Extra security check

      if (error) throw error;

      toast({
        title: "Mappning borttagen",
        description: "Fältmappning har tagits bort"
      });

      return true;
    } catch (error) {
      console.error('Error deleting mapping:', error);
      toast({
        title: "Fel vid borttagning",
        description: error instanceof Error ? error.message : "Kunde inte ta bort mappning",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const setAsDefault = useCallback(async (id: string, moduleName: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // First, remove default flag from all mappings for this module (check all aliases)
      const { getModuleAliases } = await import('@/lib/modules')
      const moduleAliases = getModuleAliases(moduleName)
      const moduleNameFilter = moduleAliases.map(a => `module_name.eq.${a}`).join(',')
      
      await supabase
        .from('field_mappings')
        .update({ is_default: false })
        .eq('user_id', user.user.id)
        .or(moduleNameFilter);

      // Then set the selected mapping as default
      const { error } = await supabase
        .from('field_mappings')
        .update({ is_default: true })
        .eq('id', id)
        .eq('user_id', user.user.id);

      if (error) throw error;

      toast({
        title: "Standard mappning",
        description: "Mappning har satts som standard för modulen"
      });

      return true;
    } catch (error) {
      console.error('Error setting default mapping:', error);
      toast({
        title: "Fel vid uppdatering",
        description: "Kunde inte sätta som standard",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    saveMapping,
    getMapping,
    getUserMappings,
    deleteMapping,
    setAsDefault,
    loading
  };
}